import { logging } from '@js-sdk/debug-tools';
import { EventEmitter } from '@spotify-internal/emitter';
import { Errors } from '../../../enums/errors';
import { Event } from '../../../enums/event';
import { EMEError } from '../../../error/eme';
import { emitEncrypted } from './emit_encrypted';
import { defer } from '../../defer';
import { LicenseRequestEvent, } from './license_request_event';
const debugLogger = logging.forTag('eme_polyfill.v0_1b');
/**
 * Polyfills `MediaKeySession`
 */
class MediaKeySession extends EventEmitter {
    /**
     * @constructor
     * @param keySystem - The name of the keysystem.
     * @param mediaElement - The media element.
     * @param prefix - The prefix to use for the APIs, if any.
     */
    constructor(keySystem, mediaElement, prefix) {
        super();
        /**
         * The media element where this MediaKeys instance is attached.
         */
        this._mediaElement = null;
        /**
         * The last deferred created for the last `update` call.
         */
        this._lastUpdateDeferred = null;
        /**
         * The last deferred created for the last `generateRequest` call.
         */
        this._lastGenerateResolver = null;
        /**
         * The session id.
         */
        this.sessionId = null;
        this._keySystem = keySystem;
        this._mediaElement = mediaElement;
        this._prefix = prefix;
        this.addEventListener = this.addListener;
        this.removeEventListener = this.removeListener;
    }
    _addPrefix(name) {
        if (this._prefix) {
            return (this._prefix +
                name.replace(/\b[a-z]/, function (l) {
                    return l.toUpperCase();
                }));
        }
        return name;
    }
    generateComplete(message) {
        this.emitEvent(new LicenseRequestEvent(message));
        if (this._lastGenerateResolver) {
            this._lastGenerateResolver.resolve(true);
            this._lastGenerateResolver = null;
        }
    }
    updateComplete() {
        if (this._lastUpdateDeferred) {
            this._lastUpdateDeferred.resolve(true);
            this._lastUpdateDeferred = null;
        }
        this.emit(Event.KEY_SESSION_STATUSES_CHANGE, null);
    }
    handleErrorEvent(e) {
        const error = new EMEError(Errors.EME_MEDIA_KEY_SESSION_V0_1B_ERROR, 'MediaKeySession v0.1b Error');
        error.debug.errorCode = e.errorCode;
        error.debug.systemCode = e.systemCode;
        if (!e.sessionId && this._lastGenerateResolver) {
            this._lastGenerateResolver.reject(error);
            this._lastGenerateResolver = null;
        }
        else if (e.sessionId && this._lastUpdateDeferred) {
            this._lastUpdateDeferred.reject(error);
            this._lastUpdateDeferred = null;
        }
        else {
            this.emit(Event.KEY_SESSION_STATUSES_CHANGE, null);
        }
    }
    /**
     * Generates a request for a license.
     *
     * @param _initDataType - The type of the init data.
     * @param initData - The init data.
     * @return A promise that will be resolved when the message is generated.
     */
    generateRequest(_initDataType, initData) {
        if (!this._mediaElement)
            return Promise.reject(new ReferenceError('InvalidState: Media keys are not attached.'));
        return new Promise((resolve, reject) => {
            this._lastGenerateResolver = { resolve, reject };
            try {
                // @ts-ignore
                this._mediaElement[this._addPrefix('generateKeyRequest')](this._keySystem, new Uint8Array(initData));
            }
            catch (e) {
                reject(e);
                this._lastGenerateResolver = null;
            }
        });
    }
    /**
     * Attaches a license to the `MediaKeySession`.
     *
     * @param license - The license to attach.
     * @return A promise that will be resolved when the license has been updated.
     */
    update(license) {
        if (!this._mediaElement)
            return Promise.reject(new ReferenceError('InvalidState: Media keys are not attached.'));
        if (this._lastUpdateDeferred) {
            // We are still updating. We need to wait until that is finished.
            const next = this.update.bind(this, license);
            return this._lastUpdateDeferred.promise.then(next, next);
        }
        const deferred = defer();
        this._lastUpdateDeferred = deferred;
        try {
            // @ts-ignore
            this._mediaElement[this._addPrefix('addKey')](this._keySystem, new Uint8Array(license), null, // License/Key Id,
            this.sessionId);
        }
        catch (e) {
            deferred.reject(e);
            this._lastUpdateDeferred = null;
        }
        return deferred.promise;
    }
    /**
     * Closes the `MediaKeySession`.
     *
     * @return A promise that will be resolved when the instance has been closed.
     */
    close() {
        if (this.sessionId && this._mediaElement) {
            try {
                // @ts-ignore
                this._mediaElement[this._addPrefix('cancelKeyRequest')](this._keySystem, this.sessionId);
            }
            catch (e) {
                debugLogger.warn('Could not close keysession', e);
            }
        }
        return Promise.resolve(true);
    }
}
/**
 * Polyfills the `MediaKeys` object.
 */
class MediaKeys {
    /**
     * @constructor
     * @param keySystem - The keysystem.
     * @param prefix - The prefix for the APIs to use, if any.
     */
    constructor(keySystem, prefix = '') {
        /**
         * The media element where this MediaKeys instance is attached.
         */
        this._mediaElement = null;
        /**
         * An array holding the sessions that are currently awaiting session ids.
         */
        this._waitingForSessionIds = [];
        /**
         * A map of MediaKeySession objects to session ids.
         */
        this._sessionMap = {};
        /**
         * A public flag that can be used to check whether the `MediaKeys` can be
         * used for more than one track.
         */
        this.shouldRefreshPerTrack = true;
        this._keySystem = keySystem;
        this._prefix = prefix;
        // Rebind event handlers
        this._onKeyMessage = this._onKeyMessage.bind(this);
        this._onKeyAdded = this._onKeyAdded.bind(this);
        this._onKeyError = this._onKeyError.bind(this);
    }
    /**
     * Attaches the instance to the `HTMLMediaElement`.
     *
     * Note that this is not part of the standard.
     *
     * @param element - The element to attach to.
     */
    attach(element) {
        this._mediaElement = element;
        const prefix = this._prefix;
        element.addEventListener(prefix + Event.V0_1B_NEEDKEY, emitEncrypted);
        const addEventListener = element.addEventListener.bind(element);
        addEventListener(prefix + Event.V0_1B_KEY_ADDED, this._onKeyAdded);
        addEventListener(prefix + Event.V0_1B_KEY_ERROR, this._onKeyError);
        addEventListener(prefix + Event.V0_1B_KEY_MESSAGE, this._onKeyMessage);
    }
    /**
     * Detaches the instance from the `HTMLMediaElement`.
     *
     * Note that this is not part of the standard.
     *
     * @param element - The element to detach from.
     */
    detach(element) {
        this._mediaElement = null;
        const prefix = this._prefix;
        element.removeEventListener(prefix + Event.V0_1B_NEEDKEY, emitEncrypted);
        const removeEventListener = element.removeEventListener.bind(element);
        removeEventListener(prefix + Event.V0_1B_KEY_ADDED, this._onKeyAdded);
        removeEventListener(prefix + Event.V0_1B_KEY_ERROR, this._onKeyError);
        removeEventListener(prefix + Event.V0_1B_KEY_MESSAGE, this._onKeyMessage);
    }
    _getSession(sessionId) {
        const session = this._sessionMap[sessionId];
        if (session) {
            return session;
        }
        const newSession = this._waitingForSessionIds.shift();
        if (newSession) {
            newSession.sessionId = sessionId;
            this._sessionMap[sessionId] = newSession;
            return newSession;
        }
        return null;
    }
    _onKeyMessage(e) {
        const session = this._getSession(e.sessionId);
        if (!session) {
            debugLogger.warn('Got keymessage without session.');
            return;
        }
        session.generateComplete(e.message);
    }
    _onKeyAdded(e) {
        const session = this._getSession(e.sessionId);
        if (!session) {
            debugLogger.warn('Got keyadded without session.');
            return;
        }
        session.updateComplete();
    }
    _onKeyError(e) {
        const session = this._getSession(e.sessionId);
        if (!session) {
            debugLogger.warn('Got keyerror without session.');
            return;
        }
        session.handleErrorEvent(e);
    }
    /**
     * Sets the server certificate for the `MediaKeys` object.
     *
     * Note that this isn't supported on the polyfill, and the returned promise is
     * guaranteed to reject.
     *
     * @return A promise that will be resolved when the server
     * certificate has been set.
     */
    setServerCertificate() {
        return Promise.reject(new EMEError(Errors.EME_CANNOT_SET_CERTIFICATE_FOR_PLATFORM, 'Cannot set server certificate on this platform.'));
    }
    /**
     * Creates a new `MediaKeySession`.
     *
     * @return The created `MediaKeySession`.
     */
    createSession() {
        const session = new MediaKeySession(this._keySystem, this._mediaElement, this._prefix);
        this._waitingForSessionIds.push(session);
        return session;
    }
}
/**
 * Assigned as the MediaElement.prototype.setMediaKeys method on IE browsers.
 *
 * @param this - The HTMLMediaElement.
 * @param mediaKeys - The MediaKeys object to assign. Can be null.
 * @return A promise that will be resolved when the media keys have been set.
 */
function _setMediaKeys(mediaKeys) {
    // Cast mediaKeys to any as it could possibly different.
    const oldKeys = this.mediaKeys;
    if (oldKeys && oldKeys !== mediaKeys) {
        oldKeys.detach(this);
    }
    delete this['mediaKeys']; // eslint-disable-line dot-notation
    this.mediaKeys = mediaKeys;
    if (mediaKeys) {
        mediaKeys.attach(this);
    }
    return Promise.resolve();
}
/**
 * A Polyfill of the MediaKeySystemAccess class.
 */
class MediaKeySystemAccess {
    /**
     * @constructor
     * @param keySystem - The name of the keySystem.
     * @param configs - An array of supported configurations to check.
     * @param prefix - The prefix to use for the APIs, if any.
     */
    constructor(keySystem, configs, prefix) {
        /**
         * The current supported configuration.
         */
        this._configuration = null;
        this.keySystem = keySystem;
        this._prefix = prefix;
        if (!this._checkConfig(configs)) {
            throw new EMEError(Errors.EME_NO_SUPPORTED_CONFIGURATION, 'No supported configurations');
        }
    }
    /**
     * Determines which configs are supported on the current environment.
     *
     * @param configs - An array of configurations to check.
     * @return `true` if there are any supported configurations, `false` otherwise.
     */
    _checkConfig(configs) {
        // WARNING! Confirm EME playback on LG WebOS 3 if you change this function.
        const keySystem = this.keySystem;
        const testVideo = document.createElement('video');
        for (let i = 0, l = configs.length; i < l; i++) {
            const config = configs[i];
            const configuration = {
                initDataTypes: config.initDataTypes,
                audioCapabilities: [],
                videoCapabilities: [],
                persistentState: 'optional',
                distinctiveIdentifier: 'optional',
                sessionTypes: ['temporary'],
                label: config.label,
            };
            let x;
            let y;
            let cap;
            let contentType;
            let hasSupported = false;
            // Audio caps
            if (config.audioCapabilities &&
                config.audioCapabilities.length &&
                configuration.audioCapabilities) {
                hasSupported = false;
                for (x = 0, y = config.audioCapabilities.length; x < y; x++) {
                    cap = config.audioCapabilities[x];
                    if (cap.contentType) {
                        contentType = cap.contentType.split(';')[0];
                    }
                    if (contentType && testVideo.canPlayType(contentType, keySystem)) {
                        configuration.audioCapabilities.push(cap);
                        hasSupported = true;
                    }
                }
            }
            // Video caps
            if (config.videoCapabilities &&
                config.videoCapabilities.length &&
                configuration.videoCapabilities) {
                hasSupported = false;
                for (x = 0, y = config.videoCapabilities.length; x < y; x++) {
                    cap = config.videoCapabilities[x];
                    contentType = cap.contentType;
                    if (cap.contentType) {
                        contentType = cap.contentType.split(';')[0];
                    }
                    if (contentType && testVideo.canPlayType(contentType, keySystem)) {
                        configuration.videoCapabilities.push(cap);
                        hasSupported = true;
                    }
                }
            }
            if (hasSupported) {
                this._configuration = configuration;
                return true;
            }
        }
        return false;
    }
    /**
     * Returns the supported configurations.
     *
     * @return A list of the supported configurations.
     */
    getConfiguration() {
        return this._configuration;
    }
    /**
     * Creates a `MediaKeys` object.
     *
     * @return The created media keys.
     */
    createMediaKeys() {
        return new Promise((resolve) => {
            resolve(new MediaKeys(this.keySystem, this._prefix));
        });
    }
}
export function setup(prefix) {
    // Polyfill `navigator.requestMediaKeySystemAccess`
    navigator.requestMediaKeySystemAccess = function (ks, conf) {
        return new Promise((resolve) => {
            resolve(new MediaKeySystemAccess(ks, conf, prefix));
        });
    };
    // Polyfill `HTMLMediaElement.prototype.setMediaKeys`
    const elementProto = HTMLMediaElement.prototype;
    delete elementProto['mediaKeys']; // eslint-disable-line dot-notation
    elementProto['setMediaKeys' // eslint-disable-line dot-notation
    ] = _setMediaKeys;
}
//# sourceMappingURL=v0_1b.js.map